import React, { memo, useState } from "react";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { FONT_SCALES, getFontScale, setFontScale } from "@/utils/fontScale";
import NotificationPanel from "../NotificationPanel";
import HelpButton from "../help/HelpButton";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { useAuth } from '@/auth/AuthContext';

const ROLE_PANEL_NAMES = {
  mudur: "MÜDÜR PANELİ",
  mudur_yardimcisi: "MÜDÜR YARDIMCISI PANELİ",
  bolge_muhendisi: "BÖLGE MÜHENDİSİ PANELİ",
  formen: "FORMEN PANELİ",
  park_gorevlisi: "PARK GÖREVLİSİ PANELİ",
  çim_ekibi: "ÇİM EKİBİ PANELİ"
};

const AppHeader = ({
  userRole,
  personnelName,
  personnelId,
  onLogout,
  // Açık olan sekme — yardım butonu içeriği buna göre çözer.
  activeTab,
  onTabChange,
  // Bildirim paneli ile sidebar rozetleri aynı veriden beslenir.
  notificationBadgesByTab = {},
  onNotificationSummaryChanged
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  // Ad-soyad kaynağı: App.js'in legacy sessionStorage state'i bazen boş gelir
  // (parkBakimAuth migrate edilmiş). AuthContext.user.personnelName JWT'den gelir
  // ve girişte daima doludur → fallback olarak kullan ki başlıkta ad HER ZAMAN çıksın.
  const { user } = useAuth();
  const displayName = (personnelName || user?.personnelName || "").trim();

  // Yazı boyutu: kök font-size'ı değiştirir, tercih tarayıcıda saklanır.
  // Başlangıç değeri index.js'te zaten uygulandı; burada yalnız seçili olanı gösteriyoruz.
  const [fontScale, setFontScaleState] = useState(getFontScale);
  const handleFontScale = (id) => setFontScaleState(setFontScale(id));

  // Yükseklik tek kaynaktan (index.css → --app-header-h) gelir; sidebar ve
  // içerik alanı aynı değişkenden hesaplar. Buradaki px'i elle değiştirme —
  // değişkeni değiştir, yerleşim kendiliğinden hizalanır.
  return (
    <div
      className="sticky top-0 z-40 flex items-center justify-between gap-3 bg-white px-4 shadow-sm border-b-[3px] border-green-500"
      style={{ height: "var(--app-header-h)" }}
    >
      <div className="flex min-w-0 items-center gap-3">
        {/* logo-belvizyon.png markanın YAZISINI da içerdiği için tek parça
            küçültülünce hem işaret hem yazı okunmaz oluyordu. logo-belvizyon-mark.png
            aynı dosyadan üretilmiş, arka planı saydam, yalnız pin+harita işareti;
            yazı ise gerçek metin — ikisi de her boyutta net kalır. */}
        <img
          src="/logo-belvizyon-mark.png?v=20260825"
          alt=""
          aria-hidden="true"
          style={{ height: "54px" }}
          className="w-auto shrink-0"
        />
        {/* Renkler logo dosyasından harf harf ölçüldü: BEL siyah; VİZYON soldan
            sağa koyudan açığa açılan bir yeşil geçişi (V #448B51 → N #69D87D);
            altçizgi de aynı geçişi izler (BEL altında siyah). */}
        <div className="hidden min-w-0 leading-tight md:block">
          <div className="whitespace-nowrap text-[18px] font-extrabold tracking-tight">
            <span
              style={{
                display: "inline-block",
                color: "#111111",
                borderBottom: "2px solid #111111"
              }}
            >
              BEL
            </span>
            <span
              style={{
                display: "inline-block",
                paddingBottom: "2px",
                backgroundImage: "linear-gradient(90deg, #488953 0%, #57A565 45%, #6EC97E 100%)",
                backgroundSize: "100% 2px",
                backgroundPosition: "0 100%",
                backgroundRepeat: "no-repeat"
              }}
            >
              <span
                style={{
                  backgroundImage: "linear-gradient(90deg, #448B51 0%, #51A460 45%, #69D87D 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "#51A460",
                  WebkitTextFillColor: "transparent"
                }}
              >
                VİZYON
              </span>
            </span>
          </div>
          <div className="mt-0.5 whitespace-nowrap text-[11px] font-semibold" style={{ color: "#57A565" }}>
            Park ve Yeşil Alan Yönetim Sistemi
          </div>
        </div>

        <div className="min-w-0 border-l-2 border-gray-300 pl-3 leading-tight">
          <h1 className="truncate text-[15px] font-bold text-gray-900">
            {ROLE_PANEL_NAMES[userRole] || "PANEL"}
          </h1>
          {displayName && (
            <p className="truncate text-[13px] font-semibold text-blue-700">{displayName}</p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {/* Yazı boyutu — ekranı yakından okuyamayan kullanıcı için. Tailwind ölçüleri
            rem tabanlı olduğundan yazı da aralıklar da orantılı büyür. */}
        <div
          className="hidden items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 p-0.5 sm:flex"
          role="group"
          aria-label="Yazı boyutu"
        >
          {FONT_SCALES.map((scale) => {
            const active = fontScale === scale.id;
            const sizeClass =
              scale.id === "sm" ? "text-[11px]" : scale.id === "md" ? "text-[13px]" : "text-[16px]";
            return (
              <button
                key={scale.id}
                type="button"
                onClick={() => handleFontScale(scale.id)}
                aria-pressed={active}
                title={`Yazı boyutu: ${scale.label}`}
                className={`flex h-7 w-7 items-center justify-center rounded font-bold leading-none transition-colors ${sizeClass} ${
                  active
                    ? "bg-green-600 text-white"
                    : "text-gray-600 hover:bg-white hover:text-gray-900"
                }`}
              >
                A
                <span className="sr-only"> — {scale.label}</span>
              </button>
            );
          })}
        </div>

        <HelpButton pageId={activeTab} onNavigate={onTabChange} />

        <NotificationPanel
          personnelId={personnelId}
          personnelName={displayName}
          badgesByTab={notificationBadgesByTab}
          onNavigate={onTabChange}
          onSummaryChanged={onNotificationSummaryChanged}
        />

        <Button variant="outline" size="sm" onClick={onLogout} className="flex items-center gap-2">
          <LogOut className="h-4 w-4" />
          Çıkış
        </Button>
      </div>
    </div>
  );
};

export default memo(AppHeader);
